import React, { useState, useEffect, useCallback } from 'react';
import { InputText, ImageUpload, DropdownField, PrimeCalendar, PrimeChips } from 'components/common/input';
import BaseModalForm from 'components/common/modal/BaseModalForm';
import ImageGallery from 'components/admin/common/ImageGallery';
import { Alert } from 'react-bootstrap';
import { useFormHandlers } from 'core/hooks/form/formHandlers';
import { MODAL_FORM_ERRORS } from 'core/constant/MODAL_FORM_ERRORS';

const EditProjectModal = ({ show, project, handleClose, handleSave, updating, errors, deleteMediaItem, requestError, initialImages, clientsOptions, productOptions }) => {
  const [formValues, setFormValues] = useState(project);
  const [galleryImages, setGalleryImages] = useState(initialImages);
  const [internalFeatures, setInternalFeatures] = useState([]);
  const [featuresToDelete, setFeaturesToDelete] = useState([]);
  const { handleChange, handleFileChangeLocal, handleRemoveImageLocal } = useFormHandlers(setFormValues);

  useEffect(() => {
    setFormValues(project);
    setGalleryImages(initialImages);
    setInternalFeatures(project?.products_used || []);
  }, [project, initialImages]);

  const handleSaveClick = useCallback(() => {
    handleSave({ ...formValues, id: project?.id }, galleryImages, internalFeatures, featuresToDelete);
  }, [formValues, project?.id, handleSave, galleryImages, internalFeatures, featuresToDelete]);

  const handleFeatureChange = (index, key, value) => {
    const newFeatures = [...internalFeatures];
    newFeatures[index] = { ...newFeatures[index], [key]: value };
    setInternalFeatures(newFeatures);
  };

  const addFeature = () => {
    setInternalFeatures([...internalFeatures, { product_id: "" }]);
  };

  const removeFeature = (index) => {
    const feature = internalFeatures[index];
    if (feature.id) {
      setFeaturesToDelete([...featuresToDelete, feature.id]);
    }
    setInternalFeatures(internalFeatures.filter((_, i) => i !== index));
  };

  return (
    <BaseModalForm show={show} title={project?.id ? "Editar Proyecto" : "Crear Proyecto"} handleClose={handleClose} handleSave={handleSaveClick} updating={updating}>
      <div className="row">
        <div className="col-md-6 form-group">
          <InputText
            label="Título"
            value={formValues?.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Ingrese el título del proyecto"
            error={errors?.title}
          />
        </div>
        <div className="col-md-6 form-group">
          <InputText
            label="Categoría"
            value={formValues?.category}
            onChange={(e) => handleChange('category', e.target.value)}
            placeholder="Ingrese la categoría del proyecto"
            error={errors?.category}
          />
        </div>
        <div className="col-md-6 form-group">
          <PrimeCalendar
            label="Fecha"
            value={formValues?.date}
            onChange={(e) => handleChange('date', e.value)}
            placeholder="Ingrese la fecha del proyecto"
            error={errors?.date}
          />
        </div>
        <div className="col-md-6 form-group">
          <InputText
            label="Ubicación"
            value={formValues?.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="Ingrese la ubicación del proyecto"
            error={errors?.location}
          />
        </div>
        <div className="col-md-6 form-group">
          <InputText
            label="Descripción"
            value={formValues?.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Ingrese la descripción del proyecto"
            error={errors?.description}
          />
        </div>
        <div className="col-md-6 form-group">
          <InputText
            label="Link de la Ubicación"
            value={formValues?.location_link}
            onChange={(e) => handleChange('location_link', e.target.value)}
            placeholder="Ingrese la descripción del proyecto"
            error={errors?.location_link}
          />
        </div>
        <div className="col-md-6 form-group">
          <PrimeChips
              label="Tags"
              value={formValues?.tags}
              onChange={(e) => handleChange('tags', e.value)}
              placeholder="Ingrese los tags del producto"
              error={errors?.tags}
            />
        </div>
        <div className="col-md-6 form-group">
          <InputText
            label="Autor"
            value={formValues?.autor}
            onChange={(e) => handleChange('autor', e.target.value)}
            placeholder="Ingrese la descripción del proyecto"
            error={errors?.autor}
          />
        </div>
        <div className="col-md-6 form-group">
          <DropdownField
            label="Cliente"
            value={formValues?.client_id}
            options={clientsOptions}
            onChange={(e) => handleChange('client_id', e.value)}
            placeholder="Seleccione el cliente"
            error={errors?.client_id}
          />
        </div>
        <div className="col-md-6 form-group">
          <ImageUpload
            label="Imagen de Banner"
            value={formValues?.banner_image}
            onFileChange={(e) => handleFileChangeLocal(e, 'banner_image')}
            onRemoveImage={() => handleRemoveImageLocal('banner_image')}
            error={errors.banner_image}
          />
        </div>
        <section className="m-0 p-0">
          <div className="row">
            <h4>Productos usados en el proyecto</h4>
            {internalFeatures?.map((feature, index) => (
              <React.Fragment key={index}>
                <div className="col-md-5">
                  <DropdownField
                    label="Producto Usado"
                    value={feature.product_id}
                    options={productOptions}
                    onChange={(e) => handleFeatureChange(index, 'product_id', e.value)}
                    placeholder="Seleccione un producto"
                    error={errors[index]?.product_id}
                  />
                </div>
                <div className="col-md-1">
                  <div className="d-flex justify-content-end align-items-center h-100">
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="delete-table delete-button me-2"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
          <button className="btn btn-primary-400 ms-3 mb-3" type="button" onClick={addFeature}>
            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
            Añadir Producto
          </button>
        </section>
        <div className="col-12 form-group">
          <h4>Galería de Imágenes del Proyecto</h4>
          <ImageGallery initialImages={galleryImages} onImagesChange={setGalleryImages} onDeleteImage={deleteMediaItem} />
        </div>
        <div className="col-12 form-group mt-3">
          {requestError &&
            <Alert variant="danger">
              {requestError?.response?.data?.message ||
               MODAL_FORM_ERRORS.PROJECT}
            </Alert>
          }
        </div>
      </div>
    </BaseModalForm>
  );
};

export default React.memo(EditProjectModal);
